import LStair from '../components/staircases/LStair.js';
import UStair from '../components/staircases/UStair.js';
import SpiralStair from '../components/staircases/SpiralStair.js';
import BespokeSpiralStair from '../components/staircases/BespokeSpiralStair.js';
import { submitForm } from '../helpers/FormSubmission.js';

const treadHeights = {
    5: "720 - 900 mm",
    6: "900 - 1080 mm",
    7: "1080 - 1260 mm",
    8: "1260 - 1440 mm",
    9: "1440 - 1620 mm",
    10: "1620 - 1800 mm",
    11: "1800 - 1980 mm",
    12: "1980 - 2160 mm",
    13: "2160 - 2340 mm",
    14: "2520 - 2700 mm",
    15: "2700 - 2880 mm",
    16: "2880 - 3060 mm",
    17: "3060 - 3240 mm",
    18: "3240 - 3420 mm",
    19: "3420 - 3600 mm"
};
const treadIncrement = 180;
const treadLengthIncrement = 285; // Each tread adds 285mm to length

// Function to calculate height range for a given number of treads
function calculateHeightRange(numTreads) {
    const minHeight = (numTreads - 1) * treadIncrement;
    const maxHeight = numTreads * treadIncrement;
    return `${minHeight} - ${maxHeight} mm`;
}

// Function to calculate length for a given number of treads
function calculateStairLength(numTreads) {
    return numTreads * treadLengthIncrement;
}

class InputListeners {
    constructor(mainSceneSetup) {
        console.log('InputListeners constructor called');
        if (InputListeners.instance) {
            return InputListeners.instance;
        }
        InputListeners.instance = this;
        this.mainSceneSetup = mainSceneSetup;
        this.isInitialized = false;
    }

    isSpiral() {
        return this.mainSceneSetup.currentStairType === 'Basic-Spiral' || 
               this.mainSceneSetup.currentStairType === 'Bespoke-Spiral';
    }

    updateSingleStairHeight(numTreads) {
        const approxHeightField = document.getElementById('approx-height');
        const heightRange = treadHeights[numTreads] || "Height data not available";
        approxHeightField.textContent = `Approx height: ${heightRange}`;
    }

    updateSingleStairLength(numTreads) {
        const approxLengthField = document.getElementById('approx-length');
        
        // Don't update the length for spiral staircases - it should remain fixed
        if (this.isSpiral()) {
            return; 
        }
        
        const length = calculateStairLength(numTreads);
        approxLengthField.textContent = `Floor Space Length: ${length} mm`;
    }

    updateTotalHeight() {
        const bottomTreads = parseInt(document.getElementById('bottom-flight-slider').value, 10);
        const topTreads = parseInt(document.getElementById('top-flight-slider').value, 10);
        const totalTreads = bottomTreads + topTreads;
        const totalHeightRange = calculateHeightRange(totalTreads);
        document.getElementById('approx-total-height').textContent = `Approx height: ${totalHeightRange}`;
    }

    updateFlightLengths() {
        const bottomTreads = parseInt(document.getElementById('bottom-flight-slider').value, 10);
        const topTreads = parseInt(document.getElementById('top-flight-slider').value, 10);
        const treadWidth = parseInt(document.getElementById('widthSlider').value, 10);
        
        // Add the landing width (which equals tread width) to flight lengths
        const bottomFlightLength = calculateStairLength(bottomTreads) + treadWidth;
        const topFlightLength = calculateStairLength(topTreads) + treadWidth;
        
        document.getElementById('bottom-flight-length').textContent = `Floor Space Length: ${bottomFlightLength} mm`;
        document.getElementById('top-flight-length').textContent = `Floor Space Length: ${topFlightLength} mm`;
    }

    // New method to update dimensions based on stair type
    updateDimensionsForStairType() {
        console.log('Updating dimensions for type:', this.mainSceneSetup.currentStairType);
        
        // Get all measurement elements
        const straightLengthElem = document.getElementById('approx-length');
        const bottomLengthElem = document.getElementById('bottom-flight-length');
        const topLengthElem = document.getElementById('top-flight-length');
        
        if (this.isSpiral()) {
            // For spiral stairs, show fixed dimension with the different format
            straightLengthElem.textContent = `Approx. Floor Space: 1.8m × 1.8m`;
            
            // Hide L/U shaped flight lengths
            if (bottomLengthElem) bottomLengthElem.parentElement.style.display = 'none';
            if (topLengthElem) topLengthElem.parentElement.style.display = 'none';
            
            // Show straight length element for spiral dimensions
            document.getElementById('stair-slider-container').style.display = '';
        } 
        else if (this.mainSceneSetup.currentStairType === 'L-shaped' || 
                 this.mainSceneSetup.currentStairType === 'U-shaped') {
            // For L and U shaped, show flight lengths
            document.getElementById('stair-slider-container').style.display = 'none';
            document.getElementById('bottom-flight-slider-container').style.display = 'block';
            document.getElementById('top-flight-slider-container').style.display = 'block';
            
            this.updateFlightLengths();
        } 
        else {
            // For straight stairs, show straight length
            document.getElementById('stair-slider-container').style.display = '';
            document.getElementById('bottom-flight-slider-container').style.display = 'none';
            document.getElementById('top-flight-slider-container').style.display = 'none';
            
            const numTreads = parseInt(document.getElementById('stair-slider').value, 10);
            this.updateSingleStairLength(numTreads);
        }
    }

    init() {
        if (this.isInitialized) {
            return;
        }
        this.isInitialized = true;

        // Set up stair type change listeners
        document.querySelectorAll('input[name="stair-type-selector"]').forEach((radio) => {
            radio.addEventListener('change', (event) => {
                // The stair type will be updated in StairStateManager
                // Wait for that to complete and then update dimensions
                setTimeout(() => {
                    this.updateDimensionsForStairType();
                    this.updateMirrorButtonVisibility();
                }, 200);
            });
        });

        // Also watch for attachment type changes which may affect visibility
        document.querySelectorAll('input[name="cantilever-option"]').forEach((radio) => {
            radio.addEventListener('change', (event) => {
                setTimeout(() => {
                    this.updateDimensionsForStairType();
                    this.updateMirrorButtonVisibility();
                }, 200);
            });
        });

        // Stair Slider (Number of Treads)
        document.getElementById('stair-slider').addEventListener('input', (event) => {
            const numTreads = parseInt(event.target.value, 10);
            document.getElementById('stair-slider').value = numTreads;
            document.getElementById('stair-input').value = numTreads;
            this.updateSingleStairHeight(numTreads);
            
            // Only update length for non-spiral staircases
            if (!this.isSpiral()) {
                this.updateSingleStairLength(numTreads);
            }
            
            this.mainSceneSetup.currentStaircase.updateStairTreads(numTreads);
            this.mainSceneSetup.calculatePrice();
        });

        document.getElementById('stair-input').addEventListener('input', (event) => {
            event.preventDefault();
            const numTreads = parseInt(event.target.value, 10);
            document.getElementById('stair-slider').value = numTreads;
            this.updateSingleStairHeight(numTreads);
            
            // Only update length for non-spiral staircases
            if (!this.isSpiral()) {
                this.updateSingleStairLength(numTreads);
            }
            
            this.mainSceneSetup.currentStaircase.updateStairTreads(numTreads);
            this.mainSceneSetup.calculatePrice();
        });

        // Bottom Flight Slider
        document.getElementById('bottom-flight-slider').addEventListener('input', (event) => {
            const bottomTreads = parseInt(event.target.value, 10);
            const topTreads = parseInt(document.getElementById('top-flight-slider').value, 10);
            document.getElementById('bottom-flight-input').value = bottomTreads;

            if (
                this.mainSceneSetup.currentStaircase instanceof UStair ||
                this.mainSceneSetup.currentStaircase instanceof LStair
            ) {
                this.mainSceneSetup.currentStaircase.updateStairTreads(bottomTreads, topTreads);
                this.mainSceneSetup.calculatePrice();
                this.updateTotalHeight();
                this.updateFlightLengths();
            }
        });

        document.getElementById('bottom-flight-input').addEventListener('input', (event) => {
            const bottomTreads = parseInt(event.target.value, 10);
            document.getElementById('bottom-flight-slider').value = bottomTreads;
            const topTreads = parseInt(document.getElementById('top-flight-slider').value, 10);

            if (
                this.mainSceneSetup.currentStaircase instanceof UStair ||
                this.mainSceneSetup.currentStaircase instanceof LStair
            ) {
                this.mainSceneSetup.currentStaircase.updateStairTreads(bottomTreads, topTreads);
                this.mainSceneSetup.calculatePrice();
                this.updateTotalHeight();
                this.updateFlightLengths();
            }
        });

        // Top Flight Slider
        document.getElementById('top-flight-slider').addEventListener('input', (event) => {
            const topTreads = parseInt(event.target.value, 10);
            const bottomTreads = parseInt(document.getElementById('bottom-flight-slider').value, 10);
            document.getElementById('top-flight-input').value = topTreads;

            if (
                this.mainSceneSetup.currentStaircase instanceof UStair ||
                this.mainSceneSetup.currentStaircase instanceof LStair
            ) {
                this.mainSceneSetup.currentStaircase.updateStairTreads(bottomTreads, topTreads);
                this.mainSceneSetup.calculatePrice();
                this.updateTotalHeight();
                this.updateFlightLengths();
            }
        });

        document.getElementById('top-flight-input').addEventListener('input', (event) => {
            const topTreads = parseInt(event.target.value, 10);
            document.getElementById('top-flight-slider').value = topTreads;
            const bottomTreads = parseInt(document.getElementById('bottom-flight-slider').value, 10);

            if (
                this.mainSceneSetup.currentStaircase instanceof UStair ||
                this.mainSceneSetup.currentStaircase instanceof LStair
            ) {
                this.mainSceneSetup.currentStaircase.updateStairTreads(bottomTreads, topTreads);
                this.mainSceneSetup.calculatePrice();
                this.updateTotalHeight();
                this.updateFlightLengths();
            }
        });

        const widthSlider = document.getElementById('widthSlider');
        const widthInput = document.getElementById('widthInput');

        // Synchronize slider and input field for tread width
        widthSlider.addEventListener('input', (event) => {
            const width = parseInt(event.target.value, 10);
            widthInput.value = width; // Update input field to match slider value
            this.mainSceneSetup.currentStaircase.updateTreadWidth(width);
            this.mainSceneSetup.calculatePrice();
            
            // Update flight lengths only for L/U staircases
            if (
                this.mainSceneSetup.currentStaircase instanceof UStair ||
                this.mainSceneSetup.currentStaircase instanceof LStair
            ) {
                this.updateFlightLengths();
            }
        });

        widthInput.addEventListener('input', (event) => {
            const width = parseInt(event.target.value, 10);
            widthSlider.value = width; // Update slider to match input field value
            this.mainSceneSetup.currentStaircase.updateTreadWidth(width);
            this.mainSceneSetup.calculatePrice();
            
            // Update flight lengths only for L/U staircases
            if (
                this.mainSceneSetup.currentStaircase instanceof UStair ||
                this.mainSceneSetup.currentStaircase instanceof LStair
            ) {
                this.updateFlightLengths();
            }
        });
        
        // Material Radio Buttons
        document.querySelectorAll('input[name="materialRadio"]').forEach((radio) => {
            radio.addEventListener('change', () => {
                const selectedMaterial = document.querySelector('input[name="materialRadio"]:checked').value;
                let texturePath;

                switch (selectedMaterial) {
                    case 'No Tread':
                        texturePath = "none";
                        break;
                    case 'American Ash':
                    case 'American Oak':
                        texturePath = '../textures/oak_veneer_01_diff_4k.jpg';
                        break;
                    default:
                        texturePath = "none";
                }

                if (this.mainSceneSetup && typeof this.mainSceneSetup.updateMaterial === 'function') {
                    this.mainSceneSetup.updateMaterial(texturePath);
                } else {
                    console.warn("MainSceneSetup or updateMaterial method not available.");
                }

                this.mainSceneSetup.calculatePrice();
            });
        });

        const submitQuoteBtn = document.getElementById('submitQuoteBtn');
        if (submitQuoteBtn) {
            submitQuoteBtn.addEventListener('click', () => {
                console.log("Submit button clicked.");
                submitForm(this.mainSceneSetup);
            });
        }
        // Mirror Toggle Button
        const mirrorButton = document.getElementById('mirror-toggle');
        if (mirrorButton) {
            mirrorButton.addEventListener('click', () => {
                if (this.mainSceneSetup.currentStaircase && typeof this.mainSceneSetup.currentStaircase.toggleMirror === 'function') {
                    this.mainSceneSetup.currentStaircase.toggleMirror();
                } else {
                    console.warn('Current staircase does not support mirroring.');
                }
            });
        }

        // Initialize dimensions based on current stair type
        this.updateDimensionsForStairType();
        
        // Update mirror button visibility based on current stair type
        this.updateMirrorButtonVisibility();
    }

    updateMirrorButtonVisibility() {
        const mirrorButton = document.getElementById('mirror-toggle');
        if (mirrorButton) {
            if (this.isSpiral()) {
                mirrorButton.style.display = '';
            } else {
                mirrorButton.style.display = 'none';
            }
        }
    }
}

export default InputListeners;